import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useLanguage } from './language-context';
import { useSession } from './session-context';
import { navItemsForGrants } from './nav-items';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { Rail } from './rail';
import { TopBar } from './top-bar';
import { SectionChipRow } from './section-chip-row';
import { CommandPalette } from './command-palette';
import { NotificationTray, useAdminInbox } from './notification-tray';
import { AccountMenu } from './account-menu';
import { useRailBadges } from './use-rail-badges';
import { deepLinkTarget } from './deep-link';
import './dash.css';

// The persistent admin-console shell (CB-D2, DC90) on the v2 visual system
// (DR-45): the rounded fixed-height shell frame with the glass rail (≥ 1200 px)
// and the scrolling workspace — sticky top bar, the section-chip row below 1200 px (replaces the v1
// bottom tab bar + More screen, CB-18), and the routed screen. It owns the three
// header overlays (⌘K palette, notification tray, account menu), the global ⌘K /
// Ctrl+K shortcut and the design `?sec=&tab=` deep-link redirect. Section grants
// (FR-A15) filter every navigation surface; enforcement stays server-side.

type Overlay = 'palette' | 'tray' | 'account' | null;

export function DashLayout() {
  const { t } = useLanguage();
  const { user, grants } = useSession();
  const { search, pathname } = useLocation();
  const [overlay, setOverlay] = useState<Overlay>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  // The workspace column is the scroll container (the window never scrolls, design
  // D:223 #om-main), so a new screen starts from its top here instead of via
  // ScrollRestoration, which only moves the window.
  useLayoutEffect(() => {
    mainRef.current?.scrollTo?.(0, 0);
  }, [pathname]);

  const items = useMemo(() => navItemsForGrants(grants), [grants]);
  const sections = useMemo(() => new Set(items.map((i) => i.section)), [items]);
  const badges = useRailBadges(sections);
  const inbox = useAdminInbox();

  const roleLabel = grants.length ? t(`admin.grant.${grants[0]}` as MessageKey) : t('admin.grant.none');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOverlay((cur) => (cur === 'palette' ? null : 'palette'));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const close = () => setOverlay(null);
  const toggle = (o: Exclude<Overlay, null>) => setOverlay((cur) => (cur === o ? null : o));

  const deepLink = deepLinkTarget(search);
  if (deepLink) return <Navigate to={deepLink} replace />;

  return (
    <div className="dash-shell">
      <Rail items={items} badges={badges} consoleTag={roleLabel} />

      <div className="dash-main" ref={mainRef}>
        <TopBar
          userName={user?.name}
          roleLabel={roleLabel}
          unread={inbox.unread}
          onSearch={() => setOverlay('palette')}
          onBell={() => toggle('tray')}
          onAccount={() => toggle('account')}
          bellOpen={overlay === 'tray'}
          accountOpen={overlay === 'account'}
        />
        <SectionChipRow items={items} />
        <main className="dash-pane">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={overlay === 'palette'} onClose={close} items={items} grants={grants} />
      <NotificationTray open={overlay === 'tray'} onClose={close} inbox={inbox} grants={grants} />
      <AccountMenu open={overlay === 'account'} onClose={close} unread={inbox.unread} />

    </div>
  );
}
